import { Link, Navigate, useParams } from 'react-router'
import { getTool, tools } from '../data/tools'
import { useLanguage } from '../i18n/LanguageContext'
import { useT } from '../i18n/useT'

const toolIcons: Record<string, React.ReactNode> = {
  'payroll-calculator': (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 7.5h7M8.5 12h.01M12 12h.01M15.5 12h.01M8.5 15.5h.01M12 15.5h.01M15.5 15.5h.01" />
    </>
  ),
  'cheque-print': (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 10h6M7 13.5h4M14.5 15.5l1.6 1.6 3-3.2" />
    </>
  ),
  verify: (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.2 12l2 2 3.8-4" />
    </>
  ),
}

export default function ToolPage() {
  const { slug } = useParams()
  const tool = getTool(slug)
  const t = useT()
  const { lang } = useLanguage()
  const fr = lang === 'fr'

  if (!tool) return <Navigate to="/tools" replace />

  const siblings = tools.filter((x) => x.slug !== tool.slug)

  return (
    <>
      {/* hero */}
      <section className="bg-gradient-to-b from-brand-50 via-white to-white">
        <div className="mx-auto max-w-7xl px-8 pt-20 pb-14">
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 text-sm font-semibold text-ink-mute hover:text-ink mb-8"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} className="w-4 h-4 rotate-180">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
            </svg>
            {t.toolPage.back}
          </Link>
          <span className="block w-fit rounded-full bg-brand-100 text-brand-800 text-xs font-semibold px-3 py-1 mb-5">
            {fr ? tool.tagFr : tool.tag}
          </span>
          <h1 className="text-5xl font-extrabold mb-5">{tool.title}</h1>
          <p className="text-lg text-ink-mute max-w-2xl">
            {fr ? tool.heroFr : tool.hero}
          </p>
        </div>
      </section>

      {/* tool workspace */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-8 pb-20">
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-mist/60 min-h-[380px] grid place-items-center p-10 text-center">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-white border border-slate-100 grid place-items-center mx-auto mb-5">
                <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={1.8} className="w-8 h-8">
                  {toolIcons[tool.slug]}
                </svg>
              </div>
              <p className="font-bold text-lg mb-1.5">{t.toolPage.workspace(tool.title)}</p>
              <p className="text-ink-mute text-[15px] max-w-md">
                {t.toolPage.workspaceNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* what it does / who it's for */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-8 pb-24 grid grid-cols-12 gap-10">
          <div className="col-span-7">
            <h2 className="text-2xl font-extrabold mb-6">{t.toolPage.does}</h2>
            <ul className="space-y-4">
              {(fr ? tool.doesFr : tool.does).map((d) => (
                <li key={d} className="flex items-start gap-3.5">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-brand-100 grid place-items-center shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth={2.4} className="w-3.5 h-3.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4 10-10" />
                    </svg>
                  </span>
                  <span className="text-[16px] leading-relaxed text-ink">{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-5">
            <div className="rounded-2xl bg-mist p-8">
              <h2 className="text-lg font-bold mb-3">{t.toolPage.audience}</h2>
              <p className="text-[15px] leading-relaxed text-ink-mute">
                {fr ? tool.audienceFr : tool.audience}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* sibling tools */}
      <section className="bg-mist">
        <div className="mx-auto max-w-7xl px-8 py-20">
          <h2 className="text-2xl font-extrabold mb-8">{t.toolPage.more}</h2>
          <div className="grid grid-cols-2 gap-6">
            {siblings.map((s) => (
              <Link
                key={s.slug}
                to={`/tools/${s.slug}`}
                className="rounded-2xl border border-slate-100 bg-white p-7 hover:border-brand-300 hover:shadow-[0_16px_40px_-16px_rgb(22_163_74/0.2)] transition-all"
              >
                <span className="inline-block rounded-full bg-brand-100 text-brand-800 text-xs font-semibold px-3 py-1 mb-4">
                  {fr ? s.tagFr : s.tag}
                </span>
                <h3 className="text-lg font-bold mb-2">{s.title}</h3>
                <p className="text-[15px] leading-relaxed text-ink-mute mb-4">
                  {fr ? s.bodyFr : s.body}
                </p>
                <span className="inline-flex items-center gap-2 font-semibold text-brand-700 text-sm">
                  {t.toolPage.openTool}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-8 py-16 text-center">
          <h2 className="text-3xl font-extrabold mb-4">{t.toolPage.cta.h}</h2>
          <p className="text-lg text-ink-mute max-w-xl mx-auto mb-8">
            {t.toolPage.cta.sub}
          </p>
          <Link
            to="/pricing"
            className="inline-block rounded-md bg-brand-600 hover:bg-brand-700 transition-colors text-white font-semibold px-7 py-3.5"
          >
            {t.toolPage.cta.button}
          </Link>
        </div>
      </section>
    </>
  )
}
